import { exists } from 'src/app/functions/exists';
import { AutoSaveViewModel } from '../../../shared/components/auto-save/auto-save-view-model';
import { TemplateDomainModel } from '../../../../domainModels/template-domain-model';
import { distinctUntilChanged, filter, map, withLatestFrom } from 'rxjs/operators';
import { MenuType } from '../../../../models/utils/dto/menu-type-definition';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { LoadingOptions } from '../../../../models/shared/loading-options';
import { Orientation } from '../../../../models/utils/dto/orientation-type';
import { EditMenuViewModel } from '../../../menu/viewModels/edit-menu-view-model';
import { ModalPublishTemplate } from '../../../../modals/modal-publish-template';
import { Injector, NgZone } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { MenuTemplate } from '../../../../models/template/dto/menu-template';
import { LocationDomainModel } from '../../../../domainModels/location-domain-model';
import { Size } from '../../../../models/shared/size';
import { LocationChangedUtils } from '../../../../utils/location-changed-utils';
import { ModalMenuTemplateRequiredLocationsChangedSaveConfirmation } from '../../../../modals/modal-menu-template-required-locations-changed-save-confirmation';
import { DistinctUtils } from '../../../../utils/distinct-utils';
import { DateUtils } from '../../../../utils/date-utils';
import { WebSocketMessageContextCommand } from '../../../../models/web-sockets/contexts/web-socket-message-context-command';
import { WebSocketService } from '../../../../services/web-socket.service';
import type { WebSocketReceiveMessage } from '../../../../models/web-sockets/messages/receive/web-socket-receive-message';
import { WebSocketReceiveReloadTemplate } from '../../../../models/web-sockets/messages/receive/web-socket-receive-reload-template';
import { ToastEvent } from '../../../../modules/toasts/models/toast-event';
import { ToastService } from '../../../../modules/toasts/service/toast.service';

export abstract class EditTemplateMenuViewModel extends AutoSaveViewModel {
  constructor(
    protected editMenuViewModel: EditMenuViewModel,
    protected templateDomainModel: TemplateDomainModel,
    protected locationDomainModel: LocationDomainModel,
    protected toastService: ToastService,
    protected webSocketService: WebSocketService,
    protected ngZone: NgZone,
    protected ngbModal: NgbModal,
    protected injector: Injector
  ) {
    super();
    this.connectToAllowAutoSaving(false);
    editMenuViewModel.connectToTemplateMode(true);
    editMenuViewModel.connectToAllowAutoSaving(false);
    editMenuViewModel.connectToAddRequest('Loading Menu Template');
    this.listenForFetchingMenuTemplateWhenLocationChanges();
    this.listenForFetchMenuTemplateCommand();
  }

  private reloadMenuTemplateToastEvent: ToastEvent | null = null;

  public readonly locId$ = this.locationDomainModel.locationId$;
  public readonly menuTemplateId$ = this.templateDomainModel.activeMenuTemplateId$;
  public readonly activeTemplate$ = this.templateDomainModel.activeMenuTemplate$;
  public readonly templateName$ = this.activeTemplate$.pipe(map(template => template?.name));
  public readonly templateStatus$ = this.activeTemplate$.pipe(map(template => template?.status));
  public readonly canDeleteTemplateMenu$ = this.editMenuViewModel.canDeleteTemplateMenu$;

  protected readonly reloadTemplateMessages$ = this.webSocketService
    .getRelevantMessageDebounced$<WebSocketReceiveReloadTemplate>(
      WebSocketMessageContextCommand.ReloadTemplate,
      msg => msg?.getGroupingKey(),
      5000
    )
    .pipe(
      withLatestFrom(this.activeTemplate$, this.editMenuViewModel.userId$),
      map(([message, menuTemplate, userId]) => ({ message, menuTemplate, userId }))
    );

  private readonly _formAutoSubmitted = new Subject<any[]>();
  public readonly formAutoSubmitted$ = this._formAutoSubmitted as Observable<any[]>;
  connectToFormAutoSubmitted = (formAutoSubmitted: any[]) => this._formAutoSubmitted.next(formAutoSubmitted);

  private readonly _changedRequiredTemplateIds = new BehaviorSubject<number[]>(undefined);
  public readonly changedRequiredTemplateIds$ = this._changedRequiredTemplateIds as Observable<number[]>;
  connectToChangedRequiredTemplateIds = (ids: number[]) => this._changedRequiredTemplateIds.next(ids);

  public readonly crumbs$ = this.activeTemplate$.pipe(
    map(menu => {
      return [menu?.getViewAllBreadcrumb(false), menu?.getEditBreadcrumb(true)];
    })
  );

  public readonly showPublishButton$ = this.editMenuViewModel.menu$.pipe(
    map(menu => menu instanceof MenuTemplate && !menu?.isPublished())
  );

  public readonly isPrintTemplate$ = this.activeTemplate$.pipe(
    map(m => m?.type === MenuType.PrintMenu),
    distinctUntilChanged()
  );

  // Auto Save Loading
  protected override readonly _autoSaveLoadingOpts = new BehaviorSubject<LoadingOptions>(
    LoadingOptions.defaultInButton()
  );
  public override readonly autoSaveLoadingOpts$ = combineLatest([
    this._autoSaveLoadingOpts,
    this.editMenuViewModel.autoSaveLoadingOpts$
  ]).pipe(map(([templateOpts, productOpts]) => templateOpts.combineWith(productOpts)));
  public override readonly autoSaving$ = this.autoSaveLoadingOpts$.pipe(map(it => it.isLoading));

  public allowLiveView$ = combineLatest([this.autoSaveLoadingOpts$, this.activeTemplate$]).pipe(
    map(([loadingOpts, menu]) => {
      const hasOrientation = menu?.displaySize?.orientation !== Orientation.NA;
      const validConfig = !loadingOpts?.isLoading && exists(menu?.displaySize) && hasOrientation;
      const validSizeForType = menu?.validSizeForType();
      return validConfig && validSizeForType;
    }),
    distinctUntilChanged()
  );

  private loadHydratedMenuTemplate$(background: boolean = false): Observable<MenuTemplate> {
    return this.editMenuViewModel.loadHydratedMenuTemplate$(background);
  }

  private loadMenuTemplate = () => this.editMenuViewModel.loadHydratedMenuTemplate();

  /* *********************** Local Threads of Execution *********************** */

  listenForFetchingMenuTemplateWhenLocationChanges(): void {
    LocationChangedUtils.loadMenuTemplate(this, this.locId$, this.menuTemplateId$, this.loadMenuTemplate);
  }

  listenForFetchMenuTemplateCommand(): void {
    this.reloadTemplateMessages$
      .pipe(
        filter(({ message, menuTemplate, userId }) => {
          if (!message?.isMessageFromDifferentUser(userId)) return false;
          return message?.isAboutThis(menuTemplate);
        })
      )
      .subscribeWhileAlive({
        owner: this,
        next: ({ message }) => {
          this.reloadMenuTemplateToast(message, 'Reload');
        }
      });
  }

  /* **************************** Publish Template **************************** */

  promptToPublishTemplate = (): void => {
    if (this.unsavedChanges) {
      this.triggerAutoFormSubmission.next();
    }
    ModalPublishTemplate.open(
      this.ngZone,
      this.ngbModal,
      this.injector,
      this.activeTemplate$,
      this.editMenuViewModel.autoSaveLoadingOpts$
    );
  };

  /* ************************************************************************** */

  /**
   * Only one of these toasts can be active at a time, and the logic to do so is built into this method.
   *
   * Note that `loadHydratedMenuTemplate$` only calls `updateHydratedMenuTemplateMap` within the template domain model,
   * because this call is usually triggered after the user has navigated into the edit menu template flow.
   * That means the non-hydrated menu template is also already loaded into memory. If we are getting
   * a command to reload the menu template, that means the menu template has changed on the backend. Therefore, we
   * also need to call `updateHydratedMenuTemplateInAllPipelines` so that the non-hydrated menu template gets updated.
   * Then, when they go back to the landing page with all menu templates, they will see the updated data in the UI.
   */
  private reloadMenuTemplateToast(message: WebSocketReceiveMessage, callToActionText: string): void {
    if (exists(this.reloadMenuTemplateToastEvent) && !this.reloadMenuTemplateToastEvent?.dismissed) return;
    const reloadMessage = message?.userMessage || 'Template has been updated.';
    this.reloadMenuTemplateToastEvent = this.toastService.publishInfoMessage(reloadMessage, null, {
      callToActionText: callToActionText,
      action: () => {
        this.reloadMenuTemplateToastEvent = null;
        this.loadHydratedMenuTemplate$().subscribe({
          next: menuTemplate => this.templateDomainModel.updateHydratedMenuTemplateInAllPipelines(menuTemplate)
        });
      }
    });
  }

  public readonly triggerFormSubmission = new Subject<boolean>();

  saveIntercept = (background: boolean) => {
    combineLatest([this.activeTemplate$, this.changedRequiredTemplateIds$]).once(
      ([template, changedRequiredTemplateIds]) => {
        const saveFormGroup = (bg: boolean = false) => {
          this.triggerFormSubmission.next(bg);
          this.connectToLastAutoSaveTimestamp(DateUtils.currentTimestamp());
        };
        const published = template?.isPublished();
        const requiredLocationsNotUpdated = !changedRequiredTemplateIds;
        const originalIdsAsStrings = template?.requiredLocationIds?.map(id => id.toString(10)) ?? [];
        const updatedIdsAsStrings = changedRequiredTemplateIds?.map(id => id.toString(10)) ?? [];
        const noRequiredLocationChanges = DistinctUtils.distinctSortedStrings(
          originalIdsAsStrings,
          updatedIdsAsStrings
        );
        if (!published || requiredLocationsNotUpdated || noRequiredLocationChanges) {
          saveFormGroup(background);
          return;
        }
        ModalMenuTemplateRequiredLocationsChangedSaveConfirmation.open(
          this.ngZone,
          this.ngbModal,
          this.injector,
          template,
          changedRequiredTemplateIds,
          saveFormGroup
        );
      }
    );
  };

  saveTemplate = (background: boolean) => {
    this.editMenuViewModel.saveMenu(background);
  };

  openTemplateLiveView = (sizeOverride?: Size) => {
    this.editMenuViewModel.openLiveViewModal(sizeOverride);
  };

  deleteTemplate = (): void => {
    this.editMenuViewModel.deleteMenu();
  };

  openDuplicateTemplateModal = (): void => {
    this.editMenuViewModel.openDuplicateMenuModal();
  };

  override destroy() {
    super.destroy();
    this.templateDomainModel.clearActiveMenuTemplate();
  }
}
